import { Badge } from '@mui/material'
import Avatar from '@mui/material/Avatar'
import { palette } from 'c1ui'
import { BotttsAvatar, ShapesAvatar } from 'components/core/avatar/UserAvatar'
import { RobotIcon } from 'components/core/icons/RobotIcon'
import { C1Link } from 'components/core/links/C1Link'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { Route } from 'lib/routes'
import { AppUserType } from 'pbts/c1/api/app/v1/app_user'
import { UserType } from 'pbts/c1/api/user/v1/user'
import React from 'react'
import { useIntl } from 'react-intl'
import { AvatarSize, getAvatarIconSize } from './AvatarSizes'
import { avatarStyles } from './styles'

type AppUserAvatarProps = {
    displayName?: string
    size?: AvatarSize
    link?: Route
    avatarBadge?: React.ReactElement
    appUserType?: AppUserType
    email?: string
    decorative?: boolean
    /** The C1 identity user type, used to render service principal avatars. */
    identityUserType?: UserType
}

export const AppUserAvatar = ({ displayName, size = 'md', link, avatarBadge, appUserType, email, decorative = false, identityUserType }: AppUserAvatarProps): React.ReactElement => {
    const intl = useIntl()
    const iconSize = getAvatarIconSize(size)

    const isServicePrincipal = identityUserType === UserType.SERVICE

    const avatar = <Badge overlap="circular" anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} badgeContent={avatarBadge}>
        <Avatar alt={displayName} sx={sxc(avatarStyles.avatar, avatarStyles.appUserAvatar, size && avatarStyles[size])}>
            {isServicePrincipal
                ? <BotttsAvatar seed={email || displayName || ''} size={size} />
                : appUserType === AppUserType.SERVICE_ACCOUNT || !displayName
                    ? <RobotIcon
                        fill={palette.grey[800]}
                        sx={{
                            fontSize: iconSize,
                        }}
                    />
                    : email
                        ? <ShapesAvatar email={email || displayName} size={size} />
                        : displayName
                            .split(' ')
                            .map((word) => word[0])
                            .join('')
                            .slice(0, 3)
            }
        </Avatar>
    </Badge>

    // When decorative, avatar is used inside a parent link - don't add another tabbable link
    if (link && !decorative) {
        let ariaLabel: string | undefined
        switch (appUserType) {
            case AppUserType.SERVICE_ACCOUNT:
                ariaLabel = intl.formatMessage({ id: 'service_account', defaultMessage: 'Service account' })
                break
            case AppUserType.SYSTEM_ACCOUNT:
                ariaLabel = intl.formatMessage({ id: 'system_account', defaultMessage: 'System account' })
                break
            default:
                ariaLabel = displayName
        }
        return <C1Link href={link} aria-label={ariaLabel}>{avatar}</C1Link>
    }

    return avatar
}
